import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { auth } from "../../firebase";
import { MOVEMENT_TYPES, buildPosition, isActive, portalLabel } from "../../services/payrollFinanceCalculator";
import { dateLabel, money } from "./shared";

export default function StatementTab({ data, portal, asOf }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const inRange = (row) => (!from || row.date >= from) && (!to || row.date <= to);
  const scope = (row) => portal === "ALL" || row.portal === portal;

  const position = useMemo(() => buildPosition({
    withdrawals: data.withdrawals.filter(inRange), movements: data.movements, repayments: data.repayments, uses: data.uses, asOf, portal
  }), [data, asOf, portal, from, to]); // eslint-disable-line

  const movements = data.movements.filter((m) => isActive(m) && scope(m) && inRange(m)).sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`));
  const repayments = data.repayments.filter((r) => isActive(r) && scope(r) && inRange(r)).sort((a, b) => a.date.localeCompare(b.date));
  const uses = data.uses.filter((u) => isActive(u) && (portal === "ALL" || !u.portal || u.portal === portal) && inRange(u)).sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`));
  const { totals } = position;
  const usedTotal = uses.reduce((sum, u) => sum + Number(u.amount || 0), 0);
  const returnedTotal = movements.filter((m) => m.type === MOVEMENT_TYPES.TO_ALM).reduce((sum, m) => sum + Number(m.amount || 0), 0);

  const exportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const add = (name, header, body) => XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([header, ...body]), name);
    add("Withdrawals",
      ["Date", "Time", "Portal", "Bank txn", "Employee", "Bank", "Account", "Amount", "Returned to ALM", "Charges", "Still with employee", "Principal repaid", "Outstanding", "Interest paid", `Interest accrued to ${asOf}`, "Payable now"],
      position.rows.map((r) => [r.withdrawal.date, r.withdrawal.time || "", r.withdrawal.portal, r.withdrawal.bankTxnId || "", r.withdrawal.employeeName, r.withdrawal.employeeBank || "", r.withdrawal.employeeAccount || "", r.principal, r.returned, r.charges, r.withEmployee, r.repaidPrincipal, r.outstanding, r.interestPaid, r.accruedInterest, r.payableNow]));
    add("Transfers & charges", ["Date", "Time", "Portal", "Type", "Employee", "ALM account / charge for", "Reference", "Amount"],
      movements.map((m) => [m.date, m.time || "", m.portal, m.type === MOVEMENT_TYPES.TO_ALM ? "Employee to ALM" : "Charge", m.employeeName, m.type === MOVEMENT_TYPES.TO_ALM ? m.companyAccount : m.chargeType, m.reference || "", m.amount]));
    add("Repayments", ["Date", "Time", "Portal", "Reference", "Principal", "Interest", "Total"], repayments.map((r) => [r.date, r.time || "", r.portal, r.reference || "", r.principalTotal, r.interestTotal, r.amount]));
    add("Use of funds", ["Date", "Time", "ALM account", "Used for", "Paid to", "Reference", "Amount"], uses.map((u) => [u.date, u.time || "", u.companyAccount, u.purpose, u.payee || "", u.reference || "", u.amount]));
    saveAs(new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], { type: "application/octet-stream" }), `Payroll_Financing_Statement_${portal}_${asOf}.xlsx`);
  };

  return (
    <>
      <section className="panel pnl-print-hide">
        <div className="panel-body">
          <div className="form-grid">
            <div className="field"><label>From date (optional)</label><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="field"><label>To date (optional)</label><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          </div>
          <div className="button-row" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>Print / Save as PDF</button>
            <button type="button" className="btn btn-muted" onClick={exportExcel}>Export Excel</button>
          </div>
        </div>
      </section>

      <article className="pnl-report" style={{ marginTop: 16 }}>
        <div className="pnl-letterhead">
          <div className="pnl-letterhead-brand">
            <img src="/alm-logo.png" alt="ALM logo" />
            <div>
              <h2 className="pnl-letterhead-title">ALM Outsourcing Services (Pvt) Ltd.</h2>
              <p className="pnl-letterhead-subtitle">Payroll Financing Statement — {portal === "ALL" ? "All portals" : portalLabel(portal)}</p>
            </div>
          </div>
          <div className="pnl-letterhead-meta">
            <strong>{from || to ? `${from ? dateLabel(from) : "start"} to ${to ? dateLabel(to) : "date"}` : "All dates"}</strong><br />
            Interest accrued to {dateLabel(asOf)}<br />
            Generated {new Date().toLocaleDateString("en-GB")} by {auth.currentUser?.email || "—"}
          </div>
        </div>

        <h3>Summary</h3>
        <table className="data-table">
          <tbody>
            <tr><td>Withdrawn from the bank</td><td>{money(totals.withdrawn)}</td></tr>
            <tr><td>Received back into ALM accounts</td><td>{money(returnedTotal)}</td></tr>
            <tr><td>Charges deducted from employee accounts</td><td>{money(totals.charges)}</td></tr>
            <tr><td>Still held in employee accounts</td><td>{money(totals.withEmployee)}</td></tr>
            <tr><td>Funds used by the company</td><td>{money(usedTotal)}</td></tr>
            <tr><td>Principal repaid to the bank</td><td>{money(totals.repaidPrincipal)}</td></tr>
            <tr><td>Interest paid to the bank</td><td>{money(totals.interestPaid)}</td></tr>
            <tr><td>Principal outstanding</td><td>{money(totals.outstanding)}</td></tr>
            <tr><td>Interest accrued to {dateLabel(asOf)}</td><td>{money(totals.accruedInterest)}</td></tr>
            <tr style={{ fontWeight: 700 }}><td>Payable to the bank if repaid on {dateLabel(asOf)}</td><td>{money(totals.payableNow)}</td></tr>
          </tbody>
        </table>

        <h3>Withdrawals</h3>
        <table className="data-table">
          <thead><tr><th>Date / time</th><th>Employee account</th><th>Amount</th><th>Returned</th><th>Charges</th><th>Outstanding</th><th>Interest to date</th></tr></thead>
          <tbody>
            {position.rows.map((r) => (
              <tr key={r.withdrawal.id}><td>{dateLabel(r.withdrawal.date, r.withdrawal.time)}</td><td>{r.withdrawal.employeeName} {r.withdrawal.employeeAccount && `(${r.withdrawal.employeeAccount})`}</td><td>{money(r.principal)}</td><td>{money(r.returned)}</td><td>{money(r.charges)}</td><td>{money(r.outstanding)}</td><td>{money(r.accruedInterest)}</td></tr>
            ))}
            <tr style={{ fontWeight: 700 }}><td colSpan={2}>Total</td><td>{money(totals.withdrawn)}</td><td>{money(totals.returned)}</td><td>{money(totals.charges)}</td><td>{money(totals.outstanding)}</td><td>{money(totals.accruedInterest)}</td></tr>
          </tbody>
        </table>

        <h3>Transfers to ALM and charges</h3>
        <table className="data-table">
          <thead><tr><th>Date / time</th><th>Type</th><th>Employee</th><th>ALM account / charge for</th><th>Reference</th><th>Amount</th></tr></thead>
          <tbody>
            {movements.map((m) => <tr key={m.id}><td>{dateLabel(m.date, m.time)}</td><td>{m.type === MOVEMENT_TYPES.TO_ALM ? "Employee → ALM" : "Charge"}</td><td>{m.employeeName}</td><td>{m.type === MOVEMENT_TYPES.TO_ALM ? m.companyAccount : m.chargeType}</td><td>{m.reference}</td><td>{money(m.amount)}</td></tr>)}
            {!movements.length && <tr><td colSpan={6}>None in this range.</td></tr>}
          </tbody>
        </table>

        <h3>Repayments to the bank</h3>
        <table className="data-table">
          <thead><tr><th>Date / time</th><th>Reference</th><th>Principal</th><th>Interest</th><th>Total</th></tr></thead>
          <tbody>
            {repayments.map((r) => <tr key={r.id}><td>{dateLabel(r.date, r.time)}</td><td>{r.reference}</td><td>{money(r.principalTotal)}</td><td>{money(r.interestTotal)}</td><td>{money(r.amount)}</td></tr>)}
            {!repayments.length && <tr><td colSpan={5}>None in this range.</td></tr>}
          </tbody>
        </table>

        <h3>Where the funds were used</h3>
        <table className="data-table">
          <thead><tr><th>Date / time</th><th>ALM account</th><th>Used for</th><th>Paid to</th><th>Amount</th></tr></thead>
          <tbody>
            {uses.map((u) => <tr key={u.id}><td>{dateLabel(u.date, u.time)}</td><td>{u.companyAccount}</td><td>{u.purpose}</td><td>{u.payee}</td><td>{money(u.amount)}</td></tr>)}
            {!uses.length && <tr><td colSpan={5}>None in this range.</td></tr>}
            {uses.length > 0 && <tr style={{ fontWeight: 700 }}><td colSpan={4}>Total used</td><td>{money(usedTotal)}</td></tr>}
          </tbody>
        </table>
        <div className="pnl-footer pnl-print-hide"><span>Confidential — prepared for internal management review only.</span></div>
      </article>
    </>
  );
}
